import { useMemo } from "react";
import type { FormValues } from "../types.js";
import { validateToken, submitForm, saveDraft, lookupField, fetchFollowOn } from "../api/formApi.js";
import type { FormBackend, FormLoadOutcome, FormSubmitOutcome } from "../api/formBackend.js";
import { serverMessage } from "../api/http.js";
import { FormExperience } from "./FormExperience.js";

/**
 * Phase 22 — the CLIENT form: the page a subject reaches from a single-use magic link.
 *
 * <p><b>Phase 329 wave 4 task 4.2.</b> Everything this page used to do — the wizard, progress,
 * conditional visibility, lookups, debounced auto-save, resume, submit and the done-state — now
 * lives in {@link FormExperience}, because the staff form is the same wizard against different
 * endpoints. What is left here is the only thing that was ever token-specific: reading the token
 * out of the URL and turning its HTTP functions into a {@link FormBackend}. The behaviour is
 * unchanged, deliberately and provably — the phase-328 suites (including the flat-contract DOM
 * golden) drive THIS component and were not touched.</p>
 *
 * <p>The token is a credential in the request BODY, never a header: it is bearer-equivalent, and
 * a URL or an `Authorization` header is exactly where it would end up in an access log.</p>
 */
export function FormPage() {
  const token = (() => {
    if (typeof window === "undefined") return "";
    return new URLSearchParams(window.location.search).get("t") ?? "";
  })();

  const backend = useMemo<FormBackend>(() => ({
    async load(): Promise<FormLoadOutcome> {
      // No token in the link at all: there is nothing to validate and nothing to ask the engine.
      if (!token) return { kind: "unavailable", variant: "invalid" };
      const outcome = await validateToken(token);
      if (!("error" in outcome)) return { kind: "ready", response: outcome };
      return outcome.theme
        ? { kind: "unavailable", variant: outcome.error, theme: outcome.theme }
        : { kind: "unavailable", variant: outcome.error };
    },
    saveDraft: (fields: FormValues) => saveDraft(token, fields),
    async submit(fields: FormValues): Promise<FormSubmitOutcome> {
      try {
        await submitForm(token, fields);
        return { kind: "submitted" };
      } catch (err) {
        // Their answers are still on the page and the submit button is live again. There is no
        // sign-in to redirect them to — a magic link IS the credential — so this surface has no
        // "signing-in" outcome.
        //
        // Card #2648 — where the engine refused with its OWN sentence (a link that outlived its
        // file: 409 `form_closed`, "This form has closed, so these answers were not submitted."),
        // that sentence is what the subject reads, verbatim, never the code; with none (a 5xx, a
        // dead network) the page says what it always said.
        const message = serverMessage(err);
        return message === undefined ? { kind: "failed" } : { kind: "failed", message };
      }
    },
    lookup: (lookupId, args) => lookupField(token, lookupId, args),
    // Phase 569 — the thank-you screen's follow-on link, asked for with the SAME just-consumed
    // token, in the body. A separate call: the submit acknowledgement carries nothing about it.
    fetchFollowOn: () => fetchFollowOn(token),
  }), [token]);

  return <FormExperience backend={backend} />;
}
